import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, Truck, RefreshCcw, ShieldCheck, Gift } from "lucide-react";
import { api, inr, formatApiError } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { Reveal, EASE } from "../components/Motion";
import ProductCard, { ComingSoonBadge } from "../components/ProductCard";
import NotifyMe from "../components/NotifyMe";
import { toast } from "sonner";

export default function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [img, setImg] = useState(0);
  const [pin, setPin] = useState("");
  const [pinResult, setPinResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [qty, setQty] = useState(1);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyProd, setNotifyProd] = useState(null);
  const [reviews, setReviews] = useState([]);
  const { addToCart } = useStore();
  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const galleryRef = useRef(null);
  const [pers, setPers] = useState({ name: "", photo: "", message: "" });
  const [uploading, setUploading] = useState(false);

  const needsPers = product ? (product.personalizable ?? product.category === "personalized") : false;

  const uploadPersPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/personalization", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPers((p) => ({ ...p, photo: data.url }));
      toast.success("Photo attached");
    } catch (e) { toast.error(formatApiError(e)); }
    setUploading(false);
  };

  const tryAdd = (goCheckout) => {
    if (needsPers) {
      if (!pers.name.trim()) { toast.error("Please add the name to personalize this gift"); return; }
      if (uploading) { toast.error("Please wait for the photo to finish uploading"); return; }
      addToCart(product, qty, { name: pers.name.trim(), photo: pers.photo || null, message: pers.message.trim() || null });
    } else {
      addToCart(product, qty);
    }
    if (goCheckout) navigate("/checkout");
  };

  useEffect(() => {
    setProduct(null); setImg(0); setPinResult(null); setPin(""); setQty(1);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      document.title = r.data.seo_title || `${r.data.name} | SAVYRON`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta && (r.data.seo_description || r.data.description)) meta.setAttribute("content", r.data.seo_description || r.data.description.slice(0, 155));
      api.post("/events", { type: "product_view", meta: { slug } }).catch(() => {});
    }).catch(() => setProduct(false));
    api.get(`/products/${slug}/reviews`).then((r) => setReviews(r.data.reviews)).catch(() => {});
  }, [slug]);

  if (product === null) return <div className="pt-40 pb-24 text-center text-[#857078]">Unwrapping…</div>;
  if (product === false) return (
    <div className="pt-40 pb-24 text-center">
      <p className="font-serif-lux text-3xl text-[#857078]">This gift doesn't exist</p>
      <Link to="/gifts" className="text-[#7E1E3F] text-sm mt-4 inline-block">← Back to all gifts</Link>
    </div>
  );

  const coming = product.availability !== "available";
  const off = product.mrp > product.price ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;

  const checkPin = async (e) => {
    e.preventDefault();
    setChecking(true);
    try {
      const { data } = await api.post("/pincode/check", { pincode: pin });
      setPinResult(data);
    } catch (err) {
      setPinResult({ serviceable: false, message: formatApiError(err) });
    }
    setChecking(false);
  };

  const onGalleryMove = (e) => {
    if (reduced || e.pointerType === "touch" || !galleryRef.current) return;
    const r = galleryRef.current.getBoundingClientRect();
    setTilt({ x: ((e.clientX - r.left) / r.width - 0.5) * 10, y: ((e.clientY - r.top) / r.height - 0.5) * 10 });
  };

  return (
    <div className="pt-24 pb-32 max-w-7xl mx-auto px-4 sm:px-6" data-testid="product-page">
      <p className="text-xs text-[#857078] mb-8 pt-4">
        <Link to="/" className="hover:text-[#7E1E3F]">Home</Link> / <Link to={`/gifts/${product.category}`} className="hover:text-[#7E1E3F] capitalize">{product.category.replace("-", " ")}</Link> / <span className="text-[#857078]">{product.name}</span>
      </p>
      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <div ref={galleryRef} onPointerMove={onGalleryMove} onPointerLeave={() => setTilt({ x: 0, y: 0 })}
            className="relative card-surface rounded-3xl p-4 overflow-hidden" data-testid="product-gallery">
            {coming && <div className="absolute top-8 left-8 z-20"><ComingSoonBadge /></div>}
            <motion.img key={img} src={product.images[img]} alt={product.name}
              initial={{ opacity: 0.4, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: EASE }}
              style={reduced ? {} : { transform: `translate(${tilt.x}px, ${tilt.y}px) scale(1.03)` }}
              className={`w-full aspect-[4/5] rounded-2xl ${product.images[img].startsWith("/assets/") ? "object-contain bg-white p-6" : "object-cover"}`} data-testid="product-main-image" />
            <div className="absolute inset-4 rounded-2xl bg-gradient-to-tr from-transparent via-transparent to-white/8 pointer-events-none" />
          </div>
          {product.images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {product.images.map((src, i) => (
                <button key={i} onClick={() => setImg(i)}
                  className={`w-20 aspect-[4/5] rounded-lg overflow-hidden border transition-colors ${i === img ? "border-[#7E1E3F]" : "border-[#7E1E3F]/15 opacity-60 hover:opacity-100"}`}
                  data-testid={`product-thumb-${i}`}>
                  <img src={src} alt="" className={`w-full h-full ${src.startsWith("/assets/") ? "object-contain bg-white" : "object-cover"}`} loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          {coming && <p className="font-mono-lux text-[11px] uppercase tracking-[0.3em] text-[#7E1E3F] mb-3">Launching Soon at Savyron</p>}
          <h1 className="font-serif-lux text-3xl sm:text-4xl text-[#2E1A22] leading-tight" data-testid="product-name">{product.name}</h1>
          <div className="mt-4 flex items-baseline gap-3 flex-wrap">
            <span className="text-3xl text-[#7E1E3F] font-semibold" data-testid="product-price">{inr(product.price)}</span>
            {product.mrp > product.price && <span className="text-lg text-[#857078] line-through" data-testid="product-mrp">{inr(product.mrp)}</span>}
            {off > 0 && <span className="text-xs bg-[#2A9D8F]/20 text-[#2A9D8F] px-2.5 py-1 rounded-full font-medium">{off}% OFF</span>}
          </div>
          {product.bundle && (
            <div className="mt-4 card-surface rounded-xl px-4 py-3 inline-block" data-testid="bundle-offer">
              <p className="text-sm text-[#5C1230] font-medium">{product.bundle.label}</p>
              <p className="text-xs text-[#857078]">Bundle pricing applies automatically at checkout</p>
            </div>
          )}
          <p className="mt-5 text-sm sm:text-base text-[#857078] leading-relaxed">{product.description}</p>

          <form onSubmit={checkPin} className="mt-6" data-testid="pincode-form">
            <p className="text-xs font-mono-lux tracking-[0.2em] uppercase text-[#857078] mb-2">Check delivery to your location</p>
            <div className="flex gap-2 max-w-sm">
              <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="Enter 6-digit pincode" inputMode="numeric"
                className="flex-1 bg-[#FBF7F1] border border-[#7E1E3F]/25 rounded-full px-4 py-2.5 text-sm text-[#2E1A22] outline-none focus:border-[#7E1E3F]/60 placeholder:text-[#857078]"
                data-testid="pincode-input" />
              <button disabled={checking || pin.length !== 6}
                className="text-xs font-semibold tracking-wider uppercase border border-[#7E1E3F]/50 text-[#5C1230] rounded-full px-5 hover:bg-[#7E1E3F]/10 transition-colors disabled:opacity-50"
                data-testid="pincode-check-button">{checking ? "…" : "Check"}</button>
            </div>
            {pinResult && (
              <p className={`mt-2 text-sm flex items-center gap-2 ${pinResult.serviceable ? "text-[#2A9D8F]" : "text-[#E63946]"}`} data-testid="pincode-result">
                {pinResult.serviceable && <Check size={14} />}
                {pinResult.message} {pinResult.eta && <span className="text-[#857078]">Estimated: {pinResult.eta}</span>}
              </p>
            )}
          </form>

          {needsPers && !coming && (
            <div className="mt-6 card-surface rounded-2xl p-5 space-y-4" data-testid="personalize-box">
              <p className="text-xs font-mono-lux tracking-[0.2em] uppercase text-[#7E1E3F]">Personalize This Gift</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[10px] text-[#857078]">Name on the gift <span className="text-[#E63946]">*</span></span>
                  <input value={pers.name} onChange={(e) => setPers({ ...pers, name: e.target.value.slice(0, 40) })} maxLength={40}
                    placeholder="e.g. Aisha"
                    className="mt-1 w-full bg-[#FBF7F1] border border-[#7E1E3F]/25 rounded-xl px-4 py-3 text-sm text-[#2E1A22] outline-none focus:border-[#7E1E3F]/60 placeholder:text-[#857078]"
                    data-testid="personalize-name" />
                </label>
                <div>
                  <span className="text-[10px] text-[#857078]">Photo to attach (optional)</span>
                  <div className="mt-1 flex items-center gap-3">
                    {pers.photo ? (
                      <div className="relative" data-testid="personalize-photo-preview">
                        <img src={pers.photo} alt="Personalization" className="w-14 h-14 rounded-xl object-cover border border-[#7E1E3F]/25" />
                        <button type="button" onClick={() => setPers({ ...pers, photo: "" })}
                          className="absolute -top-2 -right-2 bg-[#E63946] text-white rounded-full w-5 h-5 text-[10px] leading-none"
                          data-testid="personalize-photo-remove">×</button>
                      </div>
                    ) : (
                      <label className="cursor-pointer text-xs font-semibold tracking-wider uppercase border border-[#7E1E3F]/50 text-[#5C1230] rounded-full px-5 py-2.5 hover:bg-[#7E1E3F]/10 transition-colors"
                        data-testid="personalize-photo-upload">
                        {uploading ? "Uploading…" : "Upload Photo"}
                        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={uploading}
                          onChange={(e) => { uploadPersPhoto(e.target.files[0]); e.target.value = ""; }} data-testid="personalize-photo-input" />
                      </label>
                    )}
                  </div>
                </div>
              </div>
              <label className="block">
                <span className="text-[10px] text-[#857078]">Short note (optional)</span>
                <input value={pers.message} onChange={(e) => setPers({ ...pers, message: e.target.value.slice(0, 200) })} maxLength={200}
                  placeholder="e.g. Happy 25th Birthday!"
                  className="mt-1 w-full bg-[#FBF7F1] border border-[#7E1E3F]/25 rounded-xl px-4 py-3 text-sm text-[#2E1A22] outline-none focus:border-[#7E1E3F]/60 placeholder:text-[#857078]"
                  data-testid="personalize-message" />
              </label>
            </div>
          )}

          <div className="mt-8">
            {coming ? (
              <button onClick={() => setNotifyOpen(true)}
                className="w-full sm:w-auto border border-[#7E1E3F]/50 text-[#5C1230] text-sm tracking-wider uppercase font-medium px-10 py-4 rounded-full hover:bg-[#7E1E3F]/10 transition-colors"
                data-testid="product-notify-button">Notify Me at Launch</button>
            ) : (
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center border border-[#7E1E3F]/30 rounded-full">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3 text-[#857078]" data-testid="qty-minus">−</button>
                  <span className="w-8 text-center text-[#2E1A22]" data-testid="qty-display">{qty}</span>
                  <button onClick={() => setQty(Math.min(10, qty + 1))} className="px-4 py-3 text-[#857078]" data-testid="qty-plus">+</button>
                </div>
                <button onClick={() => tryAdd(false)}
                  className="border border-[#7E1E3F]/50 text-[#5C1230] text-sm tracking-wider uppercase font-medium px-8 py-4 rounded-full hover:bg-[#7E1E3F]/10 transition-colors"
                  data-testid="product-add-to-cart">Add to Cart</button>
                <button onClick={() => tryAdd(true)}
                  className="group bg-[#7E1E3F] text-[#FFFFFF] text-sm tracking-wider uppercase font-semibold px-8 py-4 rounded-full hover:bg-[#5C1230] transition-colors inline-flex items-center gap-2 active:scale-[0.98]"
                  data-testid="product-buy-now">
                  Buy Now <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            )}
          </div>

          <div className="mt-8 grid grid-cols-2 gap-3 text-xs text-[#857078]">
            <p className="flex items-center gap-2"><Truck size={14} className="text-[#7E1E3F]" /> Est. delivery 2–4 business days</p>
            <p className="flex items-center gap-2"><ShieldCheck size={14} className="text-[#7E1E3F]" /> Secure prepaid checkout</p>
            <p className="flex items-center gap-2"><Gift size={14} className="text-[#7E1E3F]" /> Gift message & wrapping at checkout</p>
            <p className="flex items-center gap-2" data-testid="exchange-eligibility">
              <RefreshCcw size={14} className="text-[#7E1E3F]" />
              {product.exchange_eligible ? "Exchange eligible (damaged/defective/wrong item)" : "Exchange not available on this product"}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-20 grid lg:grid-cols-2 gap-12">
        <Reveal>
          <div className="card-surface rounded-2xl p-7">
            <h3 className="font-serif-lux text-2xl text-[#2E1A22] mb-5">Highlights</h3>
            <ul className="space-y-3">
              {(product.highlights || []).map((h) => (
                <li key={h} className="flex gap-3 text-sm text-[#857078]"><Check size={15} className="text-[#7E1E3F] shrink-0 mt-0.5" /> {h}</li>
              ))}
            </ul>
            {product.whats_included && (
              <>
                <h3 className="font-serif-lux text-2xl text-[#2E1A22] mt-8 mb-4">What's Included</h3>
                <ul className="space-y-2">
                  {product.whats_included.map((w) => <li key={w} className="text-sm text-[#857078]">— {w}</li>)}
                </ul>
              </>
            )}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="card-surface rounded-2xl p-7">
            <h3 className="font-serif-lux text-2xl text-[#2E1A22] mb-5">Specifications</h3>
            <dl className="space-y-3">
              {Object.entries(product.specifications || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 text-sm border-b border-[#7E1E3F]/10 pb-2.5">
                  <dt className="text-[#857078]">{k}</dt><dd className="text-[#45303A] text-right">{v}</dd>
                </div>
              ))}
            </dl>
            <h3 className="font-serif-lux text-2xl text-[#2E1A22] mt-8 mb-3">Delivery & Exchange</h3>
            <p className="text-sm text-[#857078] leading-relaxed">
              Estimated delivery in 2–4 business days to serviceable pincodes. No returns; exchange only on eligible products for damaged, defective, wrong or missing items — write to founder@savyron.in with your order number and photos.
            </p>
          </div>
        </Reveal>
      </div>

      <div className="mt-16 card-surface rounded-2xl p-7" data-testid="reviews-block">
        <h3 className="font-serif-lux text-2xl text-[#2E1A22] mb-4">Reviews</h3>
        {reviews.length === 0 ? (
          <p className="font-serif-lux text-lg text-[#857078] italic">"Be the first to review this product."</p>
        ) : (
          <div className="space-y-4 mb-8">
            {reviews.map((r) => (
              <div key={r.id} className="border-b border-[#7E1E3F]/15 pb-4">
                <p className="text-[#7E1E3F] text-sm tracking-wider">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)} <span className="text-[#2A9D8F] text-xs ml-2 font-sans">Verified Purchase</span></p>
                <p className="text-sm text-[#45303A] mt-1.5 leading-relaxed">{r.text}</p>
                <p className="text-xs text-[#857078] mt-1">{r.user_name}</p>
              </div>
            ))}
          </div>
        )}
        <ReviewForm slug={product.slug} onDone={() => api.get(`/products/${slug}/reviews`).then((r) => setReviews(r.data.reviews))} />
      </div>

      {product.related?.length > 0 && (
        <div className="mt-20">
          <h3 className="font-serif-lux text-3xl text-[#2E1A22] mb-8">You May Also Love</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {product.related.map((p, i) => <ProductCard key={p.slug} product={p} index={i} onNotify={setNotifyProd} />)}
          </div>
        </div>
      )}

      {!coming && (
        <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden glass-nav p-3 flex gap-3" data-testid="mobile-sticky-buy">
          <button onClick={() => tryAdd(false)} className="flex-1 border border-[#7E1E3F]/50 text-[#5C1230] text-xs tracking-wider uppercase font-medium py-3.5 rounded-full" data-testid="mobile-add-to-cart">Add to Cart</button>
          <button onClick={() => tryAdd(true)} className="flex-1 bg-[#7E1E3F] text-[#FFFFFF] text-xs tracking-wider uppercase font-semibold py-3.5 rounded-full" data-testid="mobile-buy-now">Buy Now</button>
        </div>
      )}

      <NotifyMe product={notifyProd || (notifyOpen ? product : null)} onClose={() => { setNotifyOpen(false); setNotifyProd(null); }} />
    </div>
  );
}

function ReviewForm({ slug, onDone }) {
  const { user } = useStore();
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user === undefined) return null;
  if (!user) return (
    <p className="text-sm text-[#857078] mt-4 pt-4 border-t border-[#7E1E3F]/15">
      <Link to="/login" className="text-[#7E1E3F] hover:text-[#5C1230] font-medium" data-testid="review-login-link">Sign in</Link> to write a review. Only verified buyers can review.
    </p>
  );

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/reviews", { product_slug: slug, rating, text });
      toast.success(data.message);
      setText("");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={submit} className="mt-4 pt-5 border-t border-[#7E1E3F]/15" data-testid="review-form">
      <p className="text-xs font-mono-lux tracking-[0.2em] uppercase text-[#857078] mb-3">Write a Review</p>
      <div className="flex gap-1.5 mb-3">
        {[1, 2, 3, 4, 5].map((s) => (
          <button type="button" key={s} onClick={() => setRating(s)}
            className={`text-2xl transition-transform duration-200 hover:scale-110 ${s <= rating ? "text-[#7E1E3F]" : "text-[#E5E7EB]"}`}
            data-testid={`review-star-${s}`}>★</button>
        ))}
      </div>
      <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="How was the watch, the packaging, the moment?"
        className="w-full bg-[#FBF7F1] border border-[#E5E7EB] rounded-xl px-4 py-3 text-sm text-[#2E1A22] outline-none focus:border-[#7E1E3F]/60 placeholder:text-[#857078]/60"
        data-testid="review-text" />
      <button disabled={submitting}
        className="mt-3 text-xs font-semibold tracking-wider uppercase bg-[#7E1E3F] text-white rounded-full px-6 py-2.5 hover:bg-[#5C1230] transition-colors disabled:opacity-60"
        data-testid="review-submit">{submitting ? "Submitting…" : "Submit Review"}</button>
      <p className="text-[11px] text-[#857078] mt-2">Reviews go live after moderation. Verified Purchase badge requires a delivered order.</p>
    </form>
  );
}
